import React, { useState, useEffect } from 'react';
import { Folder, FolderPlus, X, Edit2, Trash2, MoreVertical, Pause, Play } from 'lucide-react';
import axios from 'axios';

const FolderManager = ({
  items = [], // templates, campaigns, etc.
  itemLabelField = 'name', // field name for displaying item count
  itemFolderIdField = 'folder_id', // field name for folder association
  itemCounts = null, // optional pre-computed counts { 'null': N, folderId: N } — overrides counting from items
  selectedFolderId,
  onFolderSelect,
  className = '',
  allItemsLabel = 'All Items',
  folderType = 'template', // 'template' or 'campaign'
  onFolderStatusChange = null
}) => {
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showEditFolderModal, setShowEditFolderModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolder, setEditingFolder] = useState(null);
  const [editFolderName, setEditFolderName] = useState('');
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [showFolderActions, setShowFolderActions] = useState(null);

  // Load folders on component mount
  useEffect(() => {
    loadFolders();
  }, []);

  // Close action dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.folder-actions-container')) {
        setShowFolderActions(null);
      }
    };

    if (showFolderActions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFolderActions]);

  // Load folders
  const loadFolders = async () => {
    try {
      setLoadingFolders(true);
      console.log('Loading folders for type:', folderType);
      const response = await axios.get(`/api/folders?folder_type=${folderType}`);
      setFolders(response.data || []);
    } catch (error) {
      console.error('Error loading folders:', error);
    } finally {
      setLoadingFolders(false);
    }
  };

  // Create a new folder
  const handleCreateFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;

    try {
      const response = await axios.post('/api/folders', {
        name: newFolderName.trim(),
        folder_type: folderType
      });

      if (response.data.success) {
        // Reload folders to ensure we have the complete, up-to-date list
        await loadFolders();
        setNewFolderName('');
        setShowCreateFolderModal(false);

        // Dispatch folder refresh event to notify other components
        window.dispatchEvent(new CustomEvent('foldersRefresh', {
          detail: { action: 'create', folderType }
        }));
      }
    } catch (error) {
      console.error('Error creating folder:', error);
      alert(error.response?.data?.error || 'Failed to create folder');
    }
  };

  // Edit folder
  const handleEditFolder = async (e) => {
    e.preventDefault();
    if (!editFolderName.trim() || !editingFolder) return;

    try {
      const response = await axios.put(`/api/folders/${editingFolder.id}`, {
        name: editFolderName.trim()
      });

      if (response.data.success) {
        setFolders(prev => prev.map(folder =>
          folder.id === editingFolder.id
            ? { ...folder, name: editFolderName.trim() }
            : folder
        ));
        setShowEditFolderModal(false);
        setEditingFolder(null);
        setEditFolderName('');

        // Dispatch folder refresh event to notify other components
        window.dispatchEvent(new CustomEvent('foldersRefresh', {
          detail: { action: 'edit', folderType }
        }));
      }
    } catch (error) {
      console.error('Error editing folder:', error);
      alert(error.response?.data?.error || 'Failed to edit folder');
    }
  };

  // Delete folder
  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;

    try {
      const response = await axios.delete(`/api/folders/${folderToDelete.id}`);

      if (response.data.success) {
        setFolders(prev => prev.filter(folder => folder.id !== folderToDelete.id));

        // If the deleted folder was selected, reset selection
        if (selectedFolderId === folderToDelete.id) {
          onFolderSelect(null);
        }

        setShowDeleteConfirm(false);
        setFolderToDelete(null);

        // Dispatch folder refresh event to notify other components
        window.dispatchEvent(new CustomEvent('foldersRefresh', {
          detail: { action: 'delete', folderType }
        }));
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
      alert(error.response?.data?.error || 'Failed to delete folder');
    }
  };

  // Open edit modal
  const openEditModal = (folder) => {
    setEditingFolder(folder);
    setEditFolderName(folder.name);
    setShowEditFolderModal(true);
    setShowFolderActions(null);
  };

  // Open delete confirmation
  const openDeleteConfirm = (folder) => {
    setFolderToDelete(folder);
    setShowDeleteConfirm(true);
    setShowFolderActions(null);
  };

  const handleFolderStatusChange = async (folder, action) => {
    setShowFolderActions(null);
    if (onFolderStatusChange) {
      await onFolderStatusChange(folder, action);
    }
  };

  // Get count of items in a folder
  const getItemCount = (folderId) => {
    if (itemCounts) {
      const key = folderId === null ? 'null' : folderId;
      return itemCounts[key] || 0;
    }
    if (folderId === null) {
      return items.filter(item => !item[itemFolderIdField]).length;
    }
    return items.filter(item => item[itemFolderIdField] === folderId).length;
  };

  // Create Folder Modal
  if (showCreateFolderModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Create New Folder</h2>
            <button
              onClick={() => {
                setShowCreateFolderModal(false);
                setNewFolderName('');
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleCreateFolder}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Folder Name
              </label>
              <input
                type="text"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter folder name..."
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowCreateFolderModal(false);
                  setNewFolderName('');
                }}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                disabled={!newFolderName.trim()}
              >
                Create Folder
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Edit Folder Modal
  if (showEditFolderModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Edit Folder</h2>
            <button
              onClick={() => {
                setShowEditFolderModal(false);
                setEditingFolder(null);
                setEditFolderName('');
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleEditFolder}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Folder Name
              </label>
              <input
                type="text"
                value={editFolderName}
                onChange={(e) => setEditFolderName(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter folder name..."
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowEditFolderModal(false);
                  setEditingFolder(null);
                  setEditFolderName('');
                }}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                disabled={!editFolderName.trim()}
              >
                Update Folder
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Delete Confirmation Modal
  if (showDeleteConfirm) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">Delete Folder</h2>
            <button
              onClick={() => {
                setShowDeleteConfirm(false);
                setFolderToDelete(null);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          <div className="mb-6">
            <p className="text-gray-600">
              Are you sure you want to delete the folder "{folderToDelete?.name}"?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              This action cannot be undone. Items in this folder will be moved to "All Items".
            </p>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setShowDeleteConfirm(false);
                setFolderToDelete(null);
              }}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteFolder}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Delete Folder
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-64 flex-shrink-0 ${className}`}>
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="font-medium text-gray-900">Folders</h3>
            <button
              onClick={() => setShowCreateFolderModal(true)}
              className="p-1 text-gray-400 hover:text-blue-600"
              title="Create new folder"
            >
              <FolderPlus size={16} />
            </button>
          </div>
        </div>

        <div className="p-2">
          {loadingFolders ? (
            <div className="text-center py-4 text-gray-500">Loading folders...</div>
          ) : (
            <>
              {/* All Items (no folder) */}
              <button
                onClick={() => onFolderSelect(null)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left rounded-md hover:bg-gray-100 ${
                  selectedFolderId === null ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                }`}
              >
                <Folder size={16} />
                <span className="flex flex-col">
                  <span>{allItemsLabel}</span>
                  <span className="text-xs text-gray-400">(Uncategorized)</span>
                </span>
                <span className="ml-auto text-xs text-gray-500">
                  {getItemCount(null)}
                </span>
              </button>

              {/* Folder list */}
              {folders.map(folder => (
                <div
                  key={folder.id}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-100 ${
                    selectedFolderId === folder.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  <button
                    onClick={() => onFolderSelect(folder.id)}
                    className="flex items-center gap-2 flex-1 text-left"
                  >
                    <Folder size={16} />
                    <span>{folder.name}</span>
                    <span className="ml-auto text-xs text-gray-500">
                      {getItemCount(folder.id)}
                    </span>
                  </button>

                  {/* Action menu */}
                  <div className="relative folder-actions-container">
                    <button
                      onClick={() => setShowFolderActions(showFolderActions === folder.id ? null : folder.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 rounded"
                      title="Folder actions"
                    >
                      <MoreVertical size={14} />
                    </button>

                    {showFolderActions === folder.id && (
                      <div className="absolute right-0 top-full mt-1 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                        <div className="py-1">
                          <button
                            onClick={() => openEditModal(folder)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center gap-2"
                          >
                            <Edit2 size={12} />
                            Edit
                          </button>
                          {folderType === 'campaign' && onFolderStatusChange && (
                            <>
                              <button
                                onClick={() => handleFolderStatusChange(folder, 'pause')}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 text-orange-600 flex items-center gap-2"
                              >
                                <Pause size={12} />
                                Pause
                              </button>
                              <button
                                onClick={() => handleFolderStatusChange(folder, 'resume')}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 text-green-600 flex items-center gap-2"
                              >
                                <Play size={12} />
                                Resume
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => openDeleteConfirm(folder)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 text-red-600 flex items-center gap-2"
                          >
                            <Trash2 size={12} />
                            Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FolderManager;
